(() => {
    if (window.cpQuickOptionsController) return;

    const cache = new Map();
    const directBusy = new Set();
    const state = { opener: null, backdrop: null, dialog: null, abort: null, selectedGroups: new Set(), submitting: null, timer: null };
    const selector = '[data-quick-options-open]';

    const fetchOptions = button => {
      const url = button.dataset.quickOptionsUrl;
      if (!cache.has(url)) {
        cache.set(url, fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then(response => { if (!response.ok) throw new Error('Unable to load options'); return response.text(); })
          .catch(error => { cache.delete(url); throw error; }));
      }
      return cache.get(url);
    };

    const setBusy = (button, busy) => {
      if (!button) return;
      button.classList.toggle('is--loading', busy);
      button.setAttribute('aria-busy', String(busy));
      button.setAttribute('aria-disabled', String(busy));
    };

    const showError = (scope, message) => {
      const error = scope?.querySelector('[data-card-error], [data-quick-options-error]');
      if (!error) return;
      error.textContent = message || 'Unable to add this item. Please try again.';
      error.hidden = false;
    };

    const close = (restoreFocus = true) => {
      state.abort?.abort();
      state.backdrop?.remove();
      document.body.classList.remove('cp-quick-options-open');
      clearTimeout(state.timer);
      const opener = state.opener;
      Object.assign(state, { opener: null, backdrop: null, dialog: null, abort: null, submitting: null, timer: null, selectedGroups: new Set() });
      if (restoreFocus) opener?.focus();
    };

    const maskUnselected = () => {
      state.backdrop.querySelectorAll('[data-three-option]').forEach((group, index) => {
        if (state.selectedGroups.has(`three-${index}`)) return;
        group.querySelectorAll('[data-three-current]').forEach(label => { label.textContent = 'Choose…'; });
        group.querySelectorAll('[data-three-choice]').forEach(choice => { choice.setAttribute('aria-pressed', 'false'); choice.classList.remove('cp-was-selected'); });
      });
    };

    const update = () => {
      state.backdrop.querySelectorAll('[data-two-variant-choice]:checked').forEach(() => state.selectedGroups.add('two'));
      state.backdrop.querySelectorAll('[data-three-option]').forEach((group, index) => {
        if (group.querySelector('[data-three-choice][aria-pressed="true"].cp-was-selected')) state.selectedGroups.add(`three-${index}`);
      });
      const needed = state.backdrop.querySelectorAll('[data-three-option]').length || 1;
      const ready = state.backdrop.querySelector('[data-two-variant-choice]') ? state.selectedGroups.has('two') : state.selectedGroups.size >= needed;
      const submit = state.backdrop.querySelector('[data-atc-form]');
      if (submit) { submit.disabled = !ready; submit.setAttribute('aria-disabled', String(!ready)); submit.querySelector('[data-quick-options-label]').textContent = ready ? submit.dataset.addToCartLabel : submit.dataset.selectOptionsLabel; }
      maskUnselected();
      const select = state.backdrop.querySelector('select[name="id"]');
      const variants = JSON.parse(state.backdrop.querySelector('[data-quick-options-variants]').textContent);
      const variant = variants.find(item => String(item.id) === String(select?.value));
      if (!variant) return;
      const price = state.backdrop.querySelector('[data-quick-options-price]');
      price.innerHTML = variant.compareAtPrice ? `<del>${variant.compareAtPrice}</del> <ins>${variant.price}</ins>` : variant.price;
      const image = state.backdrop.querySelector('[data-quick-options-image]');
      if (image && variant.image) { image.src = variant.image; image.alt = variant.alt; }
    };

    const open = async button => {
      if (button.getAttribute('aria-busy') === 'true') return;
      setBusy(button, true);
      button.closest('cp-product-card-options')?.querySelector('[data-card-error]')?.setAttribute('hidden', '');
      try {
        const backdrop = document.createElement('div');
        backdrop.className = 'cp-quick-options-backdrop';
        backdrop.innerHTML = await fetchOptions(button);
        const dialog = backdrop.querySelector('[role="dialog"]');
        if (!dialog) throw new Error('Options dialog missing');
        close(false);
        Object.assign(state, { opener: button, backdrop, dialog, abort: new AbortController(), selectedGroups: new Set() });
        document.body.append(backdrop);
        document.body.classList.add('cp-quick-options-open');
        window.T4SThemeSP?.ProductAjax?.init?.();
        backdrop.querySelectorAll('[data-two-variant-choice]').forEach(input => { input.checked = false; });
        update();
        dialog.focus();
      } catch (error) {
        showError(button.closest('cp-product-card-options'), error.message);
      } finally {
        setBusy(button, false);
      }
    };

    const finishDirectAdds = () => {
      directBusy.forEach(button => setBusy(button, false));
      directBusy.clear();
    };

    const finishAdd = () => {
      const hadDirectAdd = directBusy.size > 0;
      finishDirectAdds();
      if (!state.submitting && !hadDirectAdd) return;
      if (state.submitting) setBusy(state.submitting, false);
      const opener = document.querySelector('[data-drawer-options*="#t4s-mini_cart"], [data-drawer-options*="t4s-mini_cart"]');
      if (state.submitting) close(false);
      opener?.click();
    };

    document.addEventListener('pointerover', event => { const button = event.target.closest?.(selector); if (button) fetchOptions(button).catch(() => {}); }, { passive: true });
    document.addEventListener('focusin', event => { const button = event.target.closest?.(selector); if (button) fetchOptions(button).catch(() => {}); });
    document.addEventListener('click', event => {
      const directButton = event.target.closest?.('[data-action-atc]');
      if (directButton) {
        if (directBusy.has(directButton)) { event.preventDefault(); event.stopImmediatePropagation(); return; }
        directBusy.add(directButton); setBusy(directButton, true);
        directButton.parentElement?.querySelector('[data-card-error]')?.setAttribute('hidden', '');
        return;
      }
      const button = event.target.closest?.(selector);
      if (button) { event.preventDefault(); open(button); return; }
      if (!state.backdrop) return;
      if (event.target === state.backdrop || event.target.closest('[data-quick-options-close]')) { close(); return; }
      const choice = event.target.closest('[data-three-choice]');
      if (choice) { choice.classList.add('cp-was-selected'); state.selectedGroups.add(`three-${choice.closest('[data-three-option]').dataset.threeOption}`); queueMicrotask(update); }
    });
    document.addEventListener('change', event => { if (state.backdrop?.contains(event.target)) update(); });
    document.addEventListener('keydown', event => {
      if (!state.dialog) return;
      if (event.key === 'Escape') { close(); return; }
      if (event.key !== 'Tab') return;
      const focusable = [...state.dialog.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])')];
      if (!focusable.length) return;
      const first = focusable[0], last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
    document.addEventListener('submit', event => {
      const button = event.target.closest('.cp-quick-options-dialog')?.querySelector('[data-atc-form]');
      if (!button) return;
      if (state.submitting) { event.preventDefault(); event.stopImmediatePropagation(); return; }
      state.submitting = button;
      setBusy(button, true);
      state.timer = setTimeout(() => { setBusy(button, false); state.submitting = null; showError(state.backdrop, 'Unable to add this item. Please try again.'); }, 10000);
    }, true);
    ['cart:item-added', 'cart:updated', 'cart:refresh'].forEach(name => document.addEventListener(name, finishAdd));
    window.addEventListener('cart:updated', finishAdd);
    ['cart:error', 'cart:add:error', 't4s:cart:error'].forEach(name => document.addEventListener(name, event => {
      directBusy.forEach(button => { setBusy(button, false); showError(button.parentElement, event.detail?.message); });
      directBusy.clear();
      if (!state.submitting) return;
      clearTimeout(state.timer); setBusy(state.submitting, false); state.submitting = null;
      showError(state.backdrop, event.detail?.message);
    }));

    window.cpQuickOptionsController = { cache, close, open };
  })();
